import {
    resetSelection,
    selectNewItemUponRowRemoval,
} from 'owa-mail-actions/lib/mailListSelectionActions';
import { singleSelectRow } from 'owa-mail-actions/lib/singleSelectRow';
import { MailRowDataPropertyGetter, updateShouldShowSelectedRowInRp } from 'owa-mail-list-store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isReadingPanePositionOff, shouldShowReadingPane } from 'owa-mail-layout';
import { orchestrator } from 'satcheljs';
import setFocusedRowKey from '../internal/mutators/setFocusedRowKey';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

function getRowIndexToSelect(
    lastSelectedRowIndex: number,
    selectionDirection: string,
    tableViewLength: number,
    isRowNotRemovedYet?: boolean
): number {
    let indexToSelect = lastSelectedRowIndex;
    if (selectionDirection == 'Previous') {
        indexToSelect--;
    } else if (selectionDirection == 'Next' && isRowNotRemovedYet) {
        indexToSelect++;
    }

    // If the selection index ends up out of bounds, reset it to
    // either the top or the bottom of the list
    if (indexToSelect < 0) {
        indexToSelect = 0;
    } else if (indexToSelect > tableViewLength - 1) {
        indexToSelect = tableViewLength - 1;
    }

    return indexToSelect;
}

const defaultExport: (
    actionMessage: ReturnType<typeof selectNewItemUponRowRemoval>
) => void | Promise<void> = orchestrator(selectNewItemUponRowRemoval, actionMessage => {
    const {
        tableView,
        lastSelectedRowIndex,
        lastSelectedRowWasPinned,
        isRowNotRemovedYet,
        rowKeysBeingRemoved,
    } = actionMessage;
    const tableViewLength = tableView.rowKeys.length;

    // Nothing to do if the table is empty
    if (tableViewLength == 0) {
        return;
    }

    let selectionDirection = getUserConfiguration().UserOptions?.NextSelection || 'Next';
    if (
        selectionDirection === 'ReturnToView' &&
        !isReadingPanePositionOff() &&
        !isGlobalFeatureEnabled('rp-return-to-list-with-pane')
    ) {
        selectionDirection = 'Next';
    }
    if (selectionDirection === 'ReturnToView' && !isReadingPanePositionOff()) {
        const rowKeysToSkip = new Set(
            isRowNotRemovedYet
                ? (rowKeysBeingRemoved ?? [tableView.rowKeys[lastSelectedRowIndex]])
                : []
        );
        let indexToFocus = getRowIndexToSelect(
            lastSelectedRowIndex,
            'Next',
            tableViewLength,
            isRowNotRemovedYet
        );
        while (
            indexToFocus < tableViewLength &&
            rowKeysToSkip.has(tableView.rowKeys[indexToFocus])
        ) {
            indexToFocus++;
        }
        if (indexToFocus === tableViewLength) {
            indexToFocus = Math.min(lastSelectedRowIndex - 1, tableViewLength - 1);
            while (indexToFocus >= 0 && rowKeysToSkip.has(tableView.rowKeys[indexToFocus])) {
                indexToFocus--;
            }
        }
        updateShouldShowSelectedRowInRp(false);
        const rowKey = tableView.rowKeys[indexToFocus];
        if (rowKey) {
            singleSelectRow(
                tableView,
                rowKey,
                true /* isUserNavigation */,
                MailListItemSelectionSource.RowRemoval
            );
        } else {
            setFocusedRowKey(tableView, null);
            resetSelection(
                tableView,
                MailListItemSelectionSource.RowRemoval,
                true /* isUserNavigation */
            );
        }
        return;
    }

    // Get the index of the item to select
    let indexToSelect = getRowIndexToSelect(
        lastSelectedRowIndex,
        selectionDirection,
        tableViewLength,
        isRowNotRemovedYet
    );

    // When the user's setting is set to select 'previous' and  the top
    // non-pinned item was deleted, we should not select the last pinned item
    // automatically. We should instead fallback to 'next' behavior so the top
    // non-pinned item gets selected again. Note that if the user is already in
    // the pinned section we will maintain the existing behavior (and select
    // previous item)
    if (selectionDirection == 'Previous') {
        const rowKeyToSelect = tableView.rowKeys[indexToSelect];
        const wouldEnterPinnedSection =
            MailRowDataPropertyGetter.getIsPinned(rowKeyToSelect, tableView) &&
            !lastSelectedRowWasPinned;

        if (wouldEnterPinnedSection) {
            indexToSelect = getRowIndexToSelect(lastSelectedRowIndex, 'Next', tableViewLength);
        }
    }

    const rowKey = tableView.rowKeys[indexToSelect];

    let selectionSource = MailListItemSelectionSource.RowRemoval;
    // if we are in single line view and remove an item while the reading pane is open, we want to automatically open the prev/next item
    if (isReadingPanePositionOff() && shouldShowReadingPane()) {
        selectionSource = MailListItemSelectionSource.ImmersiveReadingPaneDelete;
    }

    singleSelectRow(tableView, rowKey, true /* isUserNavigation */, selectionSource);
});

export default defaultExport;
